import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { Loader2Icon } from "lucide-react";

type CompanyMobileLoadingBannerProps = {
  readonly progress: number;
  readonly className?: string;
};

export function CompanyMobileLoadingBanner({
  progress,
  className,
}: CompanyMobileLoadingBannerProps) {
  const { t } = useTranslation();
  const clamped = Math.max(0, Math.min(100, Math.round(progress)));

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-card p-3 shadow-sm",
        className,
      )}
    >
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-majorelle/10 text-majorelle shrink-0">
          <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
        </span>
        <p className="flex-1 min-w-0 text-xs font-medium text-foreground leading-snug line-clamp-2">
          {t("company.alerts.loading")}
        </p>
        <span className="text-xs font-semibold text-foreground tabular-nums shrink-0">
          {clamped}%
        </span>
      </div>
      <Progress value={clamped} className="mt-2.5 h-1.5" />
    </div>
  );
}

type CompanyMobileLoadingProps = {
  readonly progress: number;
};

export default function CompanyMobileLoading({
  progress,
}: CompanyMobileLoadingProps) {
  return (
    <div className="flex flex-col">
      <div className="px-4 pt-4 pb-3">
        <CompanyMobileLoadingBanner progress={progress} />
      </div>

      <div className="bg-gradient-to-br from-primary/5 to-transparent px-6 pt-4 pb-6 border-b border-border">
        <div className="flex items-start gap-4">
          <Skeleton className="size-14 rounded-full shrink-0" />
          <div className="flex-1 min-w-0 space-y-2.5 pt-1">
            <Skeleton className="h-6 w-3/4 max-w-[220px]" />
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <Skeleton className="h-11 rounded-md" />
          <Skeleton className="h-11 rounded-md" />
        </div>

        <div className="mt-4 rounded-xl border border-border bg-background/80 p-3 space-y-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-3/4" />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="size-9 rounded-lg" />
          ))}
        </div>
      </div>

      <div className="px-4 pt-6 pb-6 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3"
            >
              <Skeleton className="size-10 rounded-lg shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <Skeleton className="h-2.5 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          ))}
        </div>

        <Skeleton className="h-[180px] w-full rounded-xl" />

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-xl border border-border p-3"
              >
                <Skeleton className="size-10 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/5" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
